// Merges the rows several real-time sources return for the same named list into one list, for a
// RealTimeGrid in merged mode (ControlData.RTEventSources). Two sources can see the same record, for
// example two stream keys from one contact center whose queues overlap, so rows are identified by
// MergeKeyFields and each record appears once, listing every source that sent it.

// The key field that names the center. It is matched on the SOURCE's center, not the row's value: a scoped
// key can send a row without it (the agents list only binds OwnerId and live state for users inside the
// key's scope), so the same record would otherwise look different from two sources of one center.
export const DEFAULT_CENTER_FIELD = 'OwnerId';
export const DEFAULT_SOURCE_FIELD = 'Source';

const isEmpty = (v) => v === undefined || v === null || v === '';

export function mergeKey(row, keyFields, center, centerField = DEFAULT_CENTER_FIELD) {
    return keyFields.map((f) => {
        let v = f === centerField && !isEmpty(center) ? center : (row ? row[f] : undefined);
        return isEmpty(v) ? '' : String(v);
    }).join('|');
}

// "A, B" + "B, C" -> "A, B, C". Row values may already be comma-delimited (a merged queue list).
export function unionList(values) {
    const seen = new Map();
    for (const v of values) {
        if (v === undefined || v === null || v === '')
            continue;
        for (const part of String(v).split(',')) {
            const p = part.trim();
            if (p && !seen.has(p.toLowerCase()))
                seen.set(p.toLowerCase(), p);
        }
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })).join(', ');
}

// entries: [{ name, center, rows: [...], stamp }] in source order. A record seen by several sources takes
// its fields from the most recently updated source among those that sent it with live data (the center
// field filled in), falling back to any source; list fields are unioned across all of them.
export function mergeRows(entries, options) {
    const keyFields = options.keyFields || [];
    const listFields = options.listFields || [];
    const sourceField = options.sourceField || DEFAULT_SOURCE_FIELD;
    const centerField = options.centerField || DEFAULT_CENTER_FIELD;
    const merged = new Map();

    entries.forEach((entry, order) => {
        for (const row of entry.rows || []) {
            const key = keyFields.length ? mergeKey(row, keyFields, entry.center, centerField) : `${order}|${merged.size}`;
            let m = merged.get(key);
            if (!m) {
                m = { key, first: order, parts: [] };
                merged.set(key, m);
            }
            m.parts.push({ row, entry, order });
        }
    });

    const out = [];
    for (const m of merged.values()) {
        const live = m.parts.filter((p) => !isEmpty(p.row[centerField]));
        const pool = live.length ? live : m.parts;
        let base = pool[0];
        for (const p of pool)
            if ((p.entry.stamp || 0) > (base.entry.stamp || 0))
                base = p;

        const row = { ...base.row };
        if (isEmpty(row[centerField]))
            row[centerField] = base.entry.center;
        for (const f of listFields)
            row[f] = unionList(m.parts.map((p) => p.row[f]));

        const names = [];
        for (const p of [...m.parts].sort((a, b) => a.order - b.order))
            if (!names.includes(p.entry.name))
                names.push(p.entry.name);
        row[sourceField] = names.join(', ');
        row._id = m.key;
        out.push(row);
    }
    return out;
}

// Same rule the server applies to "_any_": case and diacritic insensitive substring over the row's
// top-level string fields, which here includes the Source names.
const fold = (s) => String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export function matchesQuickSearch(row, text) {
    if (!text)
        return true;
    const needle = fold(text);
    for (const k in row) {
        const v = row[k];
        if (typeof v === 'string' && fold(v).includes(needle))
            return true;
    }
    return false;
}

// Sort on the raw field value (what the server sorts on), not the formatted cell text, so a duration
// shown as "701:23:15" sorts by its start time. Empty values sort last in either direction (ag-grid
// negates the comparator for a descending sort, so pass descending to keep them last).
export function compareRaw(a, b, descending = false) {
    const ea = isEmpty(a);
    const eb = isEmpty(b);
    if (ea || eb)
        return ea && eb ? 0 : (ea ? 1 : -1) * (descending ? -1 : 1);
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    if (typeof a === 'boolean' || typeof b === 'boolean')
        return (a ? 1 : 0) - (b ? 1 : 0);
    const da = Date.parse(a), db = Date.parse(b);
    if (typeof a === 'string' && typeof b === 'string' && /^\d{4}-\d\d-\d\d/.test(a) && /^\d{4}-\d\d-\d\d/.test(b) && !isNaN(da) && !isNaN(db))
        return da - db;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}
